import React, { useState, useEffect } from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import { IonApp, IonRouterOutlet } from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";

/* CSS for international phone drop down */
import "intl-tel-input/build/css/intlTelInput.css";

/* Core CSS required for Ionic components to work properly */
import "@ionic/react/css/core.css";

/* Basic CSS for apps built with Ionic */
import "@ionic/react/css/normalize.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/typography.css";

/* Optional CSS utils that can be commented out */
import "@ionic/react/css/padding.css";
import "@ionic/react/css/float-elements.css";
import "@ionic/react/css/text-alignment.css";
import "@ionic/react/css/text-transformation.css";
import "@ionic/react/css/flex-utils.css";
import "@ionic/react/css/display.css";

/* Theme variables */
import "./theme/variables.css";

/* Mother css */
import "./css/MotherCSS.css";

/* Phone css */
import "react-phone-input-2/lib/style.css";

// Cache System
import { QueryClient, QueryClientProvider } from "react-query";

import useAuthStore from "./states/useAuth";
import useGeneralStore from "./states/useGeneral";

import log from "./utils/log";
import moment from "moment-timezone";

/*Pages*/
import PrivateRoute from "./PrivateRoute";
import Error403 from "./pages/error/403";
import Login from "./pages/login";
import Home from "./pages/home";
import MerchantUserForgotPassword from "./pages/forgotPassword";

import SignUp from "./pages/signUp";
import TwoFa from "./pages/twoFa";
import GetStarted from "./pages/getStarted";
import GetStartedShop1 from "./pages/getStartedShop1";
import GetStartedShop2 from "./pages/getStartedShop2";

/**
 * Order Watcher
 */
import OrderWatcher_RecentOrder from "./pages/orderWatcher/dashboard/recentOrder";
import OrderWatcher_Report from "./pages/orderWatcher/dashboard/report";

/**
 * Approver
 */
import ApproverShopRequset from "./pages/approver/Shop/ShopRequest";
import ApproverSingleShopRequset from "./pages/approver/Shop/SingleShopRequset";
import ApproverProductRequset from "./pages/approver/Product/ProductRequest";
import ApproverSingleProductRequset from "./pages/approver/Product/SingleProductRequset";
import ApproverAdBanner from "./pages/approver/Ad/AdBanner";
import ApproverPushNotification from "./pages/approver/PushNotification/PushNotification";
import ApproverWelcome from "./pages/approver/Welcome/Welcome";
import ApproverSingleWelcome from "./pages/approver/Welcome/SingleWelcome";
import ApproverReport_Distribute from "./pages/approver/Report/Distribute";

/**
 * Merchant
 */
import MerchantHome from "./pages/merchantUser/Home";
import MerchantUser from "./pages/merchantUser/User/User";
import MerchantUserSingleUser from "./pages/merchantUser/User/SingleUser";
import MerchantShop from "./pages/merchantUser/Shop/Shop";
import MerchantUserSingleShopRequset from "./pages/merchantUser/Shop/SingleShopRequest";
import MerchantSingleShop from "./pages/merchantUser/Shop/SingleShop";
import MerchantProduct from "./pages/merchantUser/Product/Product";
import MerchantUserSingleProductRequset from "./pages/merchantUser/Product/SingleProductRequest";
import MerchantSingleProduct from "./pages/merchantUser/Product/SingleProduct";
import MerchantUserEstamp from "./pages/merchantUser/Estamp/Estamp";
import MerchantSetting from "./pages/merchantUser/Setting";
import MerchantReport_Distribute from "./pages/merchantUser/Report/Distribute";
import MerchantReport_Redeem from "./pages/merchantUser/Report/Redeem";
import MerchantReport_Transfer from "./pages/merchantUser/Report/Transfer";
import MerchantReport_Estamp_Earn from "./pages/merchantUser/Report/Estamp/Earn";
import MerchantReport_Estamp_Transfer from "./pages/merchantUser/Report/Estamp/Transfer";
import MerchantReport_Estamp_Redeem from "./pages/merchantUser/Report/Estamp/Redeem";
import MerchantReport_Discount from "./pages/merchantUser/Report/Discount";
import DownloadApp from "./pages/DownloadApp";
import MerchantPromotionCode from "./pages/merchantUser/PromotionCode/PromotionCode";
import MerchantSinglePromotionCode from "./pages/merchantUser/PromotionCode/SinglePromotionCode";
import MerchantAdBanner from "./pages/merchantUser/Ad/AdBanner";
import MerchantPushNotification from "./pages/merchantUser/PushNotification/PushNotification";
import MerchantWelcome from "./pages/merchantUser/Welcome/Welcome";
import MerchantSingleWelcome from "./pages/merchantUser/Welcome/SingleWelcome";
import MerchantReferral from "./pages/merchantUser/Referral/Referral";
import MerchantSingleReferral from "./pages/merchantUser/Referral/SingleReferral";

/*Language set up*/
import "./i18n";
import { useTranslation } from "react-i18next";

import Config from "./components/config";
import privatePath from "./data/privatePath";
import axios from "axios";
import metaData from "./data/metaData";

/*setup env*/
require("dotenv").config();

const App = () => {
  const queryClient = new QueryClient(); // Create a QueryClient instance
  const { t, i18n } = useTranslation();

  const { auth, user: userFromStore, updateAuth } = useAuthStore();
  const { language, changeLanguage } = useGeneralStore();

  const checkJwt = async () => {
    if (!privatePath.includes(window.location.pathname)) {
      log.debug("No need to check JWT");
      return;
    }

    const isTokenValid = auth?.passed && auth?.jwt && moment(auth?.expiryAt).diff(moment(), "seconds") > 0;

    if (isTokenValid) {
      log.debug("No need to check JWT");
      return;
    }

    try {
      const res = await axios.get(`${metaData.serverURL}checkJwt?role=merchantUser`, {
        headers: { Authorization: auth.jwt },
      });
      log.debug(res);

      let { user, token } = res.data;
      if (!user?.role || !token) {
        throw new Error("token / role is missing");
      }

      updateAuth({ passed: true, jwt: token, role: user.role });
    } catch (error) {
      log.debug("JWT check failed, logging out");
      updateAuth({ passed: false });
    }
  };

  const initLang = () => {
    log.debug(i18n.language);
    if (language == null) changeLanguage(i18n.language);
  };

  const initTimezone = () => {
    log.debug(`init timezone: ${metaData.timezone}`);
    moment.tz.setDefault(metaData.timezone);
  };

  useEffect(() => {
    // globalActions.keep("default");
    initLang();
    initTimezone();
    checkJwt();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <IonApp>
        <IonReactRouter>
          <IonRouterOutlet animated="false">
            {/* Switch is a must to prevent rendering two Route at the same time */}
            <Switch>
              {/* Public route */}
              <Route path="/login" component={Login} exact />
              <Route path="/forgotPassword" component={MerchantUserForgotPassword} exact />
              <Route path="/error/403" component={Error403} exact />
              <Route path="/downloadApp" component={DownloadApp} exact />
              <Route path="/signUp" component={SignUp} exact />
              <Route path="/twoFa" component={TwoFa} exact />

              {/* Private routes */}
              <PrivateRoute path="/home" component={Home} exact />
              <PrivateRoute path="/orderWatcher/dashboard/recentOrder" component={OrderWatcher_RecentOrder} exact />
              <PrivateRoute path="/orderWatcher/dashboard/report" component={OrderWatcher_Report} exact />

              {/* Private routes for approver */}
              <PrivateRoute
                path="/approver/shop/request"
                component={ApproverShopRequset}
                exact
                hasLayout
                allowedRoles={["approver"]}
              />
              <PrivateRoute
                path="/approver/shop/request/:id"
                component={ApproverSingleShopRequset}
                exact
                hasLayout
                allowedRoles={["approver"]}
              />
              <PrivateRoute
                path="/approver/product/request/"
                component={ApproverProductRequset}
                exact
                hasLayout
                allowedRoles={["approver"]}
              />
              <PrivateRoute
                path="/approver/product/request/:id"
                component={ApproverSingleProductRequset}
                exact
                hasLayout
                allowedRoles={["approver"]}
              />
              <PrivateRoute
                path="/approver/ad/banner"
                component={ApproverAdBanner}
                exact
                hasLayout
                allowedRoles={["approver"]}
              />
              <PrivateRoute
                path="/approver/pushNotification"
                component={ApproverPushNotification}
                exact
                hasLayout
                allowedRoles={["approver"]}
              />
              <PrivateRoute
                path="/approver/welcome"
                component={ApproverWelcome}
                exact
                hasLayout
                allowedRoles={["approver"]}
              />
              <PrivateRoute
                path="/approver/welcome/:id"
                component={ApproverSingleWelcome}
                exact
                hasLayout
                allowedRoles={["approver"]}
              />
              <PrivateRoute
                path="/approver/report/distribute"
                component={ApproverReport_Distribute}
                exact
                hasLayout
                allowedRoles={["approver"]}
              />

              {/* Private routes for merchantUser */}
              <PrivateRoute path="/get-started" component={GetStarted} exact allowedRoles={["merchantUser"]} />
              <PrivateRoute
                path="/get-started/shop/1"
                component={GetStartedShop1}
                exact
                allowedRoles={["merchantUser"]}
              />
              <PrivateRoute
                path="/get-started/shop/2"
                component={GetStartedShop2}
                exact
                allowedRoles={["merchantUser"]}
              />
              <PrivateRoute
                path="/merchantUser/home"
                component={MerchantHome}
                exact
                hasLayout
                allowedRoles={["merchantUser"]}
              />
              <PrivateRoute
                path="/merchantUser/setting"
                component={MerchantSetting}
                exact
                hasLayout
                allowedRoles={["merchantUser"]}
              />
              <PrivateRoute
                path="/merchantUser/report/distribute"
                component={MerchantReport_Distribute}
                exact
                hasLayout
                allowedRoles={["merchantUser"]}
              />
              <PrivateRoute
                path="/merchantUser/report/redeem"
                component={MerchantReport_Redeem}
                exact
                hasLayout
                allowedRoles={["merchantUser"]}
              />
              <PrivateRoute
                path="/merchantUser/report/transfer"
                component={MerchantReport_Transfer}
                exact
                hasLayout
                allowedRoles={["merchantUser"]}
              />
              <PrivateRoute
                path="/merchantUser/report/coin/earn"
                component={MerchantReport_Estamp_Earn}
                exact
                hasLayout
                allowedRoles={["merchantUser"]}
              />
              <PrivateRoute
                path="/merchantUser/report/coin/transfer"
                component={MerchantReport_Estamp_Transfer}
                exact
                hasLayout
                allowedRoles={["merchantUser"]}
              />
              <PrivateRoute
                path="/merchantUser/report/coin/redeem"
                component={MerchantReport_Estamp_Redeem}
                exact
                hasLayout
                allowedRoles={["merchantUser"]}
              />
              <PrivateRoute
                path="/merchantUser/report/discount"
                component={MerchantReport_Discount}
                exact
                hasLayout
                allowedRoles={["merchantUser"]}
              />

              {/* User */}
              <PrivateRoute
                path="/merchantUser/user"
                component={MerchantUser}
                exact
                hasLayout
                allowedRoles={["merchantUser"]}
              />
              <PrivateRoute
                path="/merchantUser/user/:id"
                component={MerchantUserSingleUser}
                exact
                hasLayout
                allowedRoles={["merchantUser"]}
              />
              {/* Shop */}
              <PrivateRoute
                path="/merchantUser/shop"
                component={MerchantShop}
                exact
                hasLayout
                allowedRoles={["merchantUser"]}
              />
              <PrivateRoute
                path="/merchantUser/shop/request/:id"
                component={MerchantUserSingleShopRequset}
                exact
                hasLayout
                allowedRoles={["merchantUser"]}
              />
              <PrivateRoute
                path="/merchantUser/shop/:id"
                component={MerchantSingleShop}
                exact
                hasLayout
                allowedRoles={["merchantUser"]}
              />
              {/* Product */}
              <PrivateRoute
                path="/merchantUser/product"
                component={MerchantProduct}
                exact
                hasLayout
                allowedRoles={["merchantUser"]}
              />
              <PrivateRoute
                path="/merchantUser/product/request/:id"
                component={MerchantUserSingleProductRequset}
                exact
                hasLayout
                allowedRoles={["merchantUser"]}
              />
              <PrivateRoute
                path="/merchantUser/product/:id"
                component={MerchantSingleProduct}
                exact
                hasLayout
                allowedRoles={["merchantUser"]}
              />
              {/* Coin */}
              <PrivateRoute
                path="/merchantUser/coin"
                component={MerchantUserEstamp}
                exact
                hasLayout
                allowedRoles={["merchantUser"]}
              />
              {/* PromotionCode */}
              <PrivateRoute
                path="/merchantUser/promotionCode"
                component={MerchantPromotionCode}
                exact
                hasLayout
                allowedRoles={["merchantUser"]}
              />
              <PrivateRoute
                path="/merchantUser/promotionCode/:id"
                component={MerchantSinglePromotionCode}
                exact
                hasLayout
                allowedRoles={["merchantUser"]}
              />

              {/* Ad */}
              <PrivateRoute
                path="/merchantUser/ad/banner"
                component={MerchantAdBanner}
                exact
                hasLayout
                allowedRoles={["merchantUser"]}
              />

              {/* PushNotification */}
              <PrivateRoute
                path="/merchantUser/pushNotification"
                component={MerchantPushNotification}
                exact
                hasLayout
                allowedRoles={["merchantUser"]}
              />

              {/* Welcome */}
              <PrivateRoute
                path="/merchantUser/welcome"
                component={MerchantWelcome}
                exact
                hasLayout
                allowedRoles={["merchantUser"]}
              />
              <PrivateRoute
                path="/merchantUser/welcome/:id"
                component={MerchantSingleWelcome}
                exact
                hasLayout
                allowedRoles={["merchantUser"]}
              />

              {/* Referral */}
              <PrivateRoute
                path="/merchantUser/referral"
                component={MerchantReferral}
                exact
                hasLayout
                allowedRoles={["merchantUser"]}
              />
              <PrivateRoute
                path="/merchantUser/referral/:id"
                component={MerchantSingleReferral}
                exact
                hasLayout
                allowedRoles={["merchantUser"]}
              />

              <Redirect path="/" to={{ pathname: "/login" }} />
            </Switch>
          </IonRouterOutlet>

          <Config />
        </IonReactRouter>
      </IonApp>
    </QueryClientProvider>
  );
  /*Night Work*/
};

export default App;
